import { z } from 'zod';
import { AxonEventSchema } from './event.types.js';
import { AgentSchema } from './agent.types.js';
import { AgentDraftSchema } from './agent-draft.types.js';
import { QueuedMessageSchema } from '@runloop/reflex-contract';
import { UsageSnapshotSchema } from './usage.types.js';
import { MaintenanceStateSchema } from './config.types.js';
import {
  SandboxMetricSampleSchema,
  SandboxThresholdsSchema,
  SandboxProcessSampleSchema,
} from './sandbox-metrics.types.js';

// --- Client → Server messages ---

export const WsSubscribeSchema = z.object({
  type: z.literal('subscribe'),
  streamId: z.string(),
  /**
   * Replay only history with `sequence > afterSequence` after subscribing
   * (a client that already fetched its initial window subscribes at the
   * window head, making the socket live-only). Omitted → full history
   * replay, the legacy behavior. Events without a sequence always replay
   * — they cannot be placed against the cursor, and dropping one could
   * hide a locally-synthesized state frame; dedup by event id.
   */
  afterSequence: z.number().int().nonnegative().optional(),
});

export const WsUnsubscribeSchema = z.object({
  type: z.literal('unsubscribe'),
  streamId: z.string(),
});

/**
 * Platform-admin-only opt-in to the usage snapshot stream. The snapshot is
 * host-wide (every tenant's users, orgs, and running agents), so the server
 * gates this on platform admin (super or app admin) — org-level `admin:read`
 * is not enough. It pushes a snapshot immediately, then every few seconds
 * while the socket remains subscribed.
 */
export const WsUsageSubscribeSchema = z.object({
  type: z.literal('usage:subscribe'),
});

export const WsUsageUnsubscribeSchema = z.object({
  type: z.literal('usage:unsubscribe'),
});

/**
 * Application-level liveness probe. Browsers cannot send protocol-level
 * WebSocket pings from JS, so the client sends this as a regular message
 * on a heartbeat interval and the server answers with `{ type: 'pong' }`.
 * The round trip guarantees a healthy connection carries app-visible
 * traffic at least once per interval, which lets the client's liveness
 * watchdog distinguish "quiet but alive" from "half-open" (a dropped
 * socket the browser never received a close frame for).
 */
export const WsPingSchema = z.object({
  type: z.literal('ping'),
});

/**
 * The monitoring panel mounts -> sends `sandbox:watch`, unmounts -> sends
 * `sandbox:unwatch`. The server bridge service keeps a per-agent viewer
 * count; first watcher triggers `metrics.subscribe` to the on-box bridge,
 * last watcher gone triggers `metrics.unsubscribe`. So live metric
 * streaming end-to-end (box -> server -> browser) only runs while at least
 * one user has the panel open.
 */
export const WsSandboxWatchSchema = z.object({
  type: z.literal('sandbox:watch'),
  agentId: z.string(),
});

export const WsSandboxUnwatchSchema = z.object({
  type: z.literal('sandbox:unwatch'),
  agentId: z.string(),
});

export const WsClientMessageSchema = z.discriminatedUnion('type', [
  WsSubscribeSchema,
  WsUnsubscribeSchema,
  WsUsageSubscribeSchema,
  WsUsageUnsubscribeSchema,
  WsSandboxWatchSchema,
  WsSandboxUnwatchSchema,
  WsPingSchema,
]);
export type WsClientMessage = z.infer<typeof WsClientMessageSchema>;

// --- Server → Client messages ---

export const WsEventMessageSchema = z.object({
  type: z.literal('event'),
  event: AxonEventSchema,
});

export const WsAgentUpdatedSchema = z.object({
  type: z.literal('agent:updated'),
  agent: AgentSchema,
});

export const WsAgentCreatedSchema = z.object({
  type: z.literal('agent:created'),
  agent: AgentSchema,
});

/**
 * An agent run was deleted (`DELETE /agents/:id`). The durable row is
 * tombstoned and hidden, so clients drop it from every list and cache.
 */
export const WsAgentDeletedSchema = z.object({
  type: z.literal('agent:deleted'),
  agentId: z.string(),
});

export const WsAgentDraftCreatedSchema = z.object({
  type: z.literal('agent-draft:created'),
  draft: AgentDraftSchema,
});

export const WsAgentDraftUpdatedSchema = z.object({
  type: z.literal('agent-draft:updated'),
  draft: AgentDraftSchema,
});

export const WsAgentDraftDeletedSchema = z.object({
  type: z.literal('agent-draft:deleted'),
  draftId: z.string(),
});

export const WsSubscribedSchema = z.object({
  type: z.literal('subscribed'),
  streamId: z.string(),
});

export const WsUnsubscribedSchema = z.object({
  type: z.literal('unsubscribed'),
  streamId: z.string(),
});

export const WsErrorSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

export const WsVersionSchema = z.object({
  type: z.literal('version'),
  version: z.string(),
  /** Git commit SHA of the running server build, when the host bakes it in. */
  commit: z.string().optional(),
});

/**
 * Pushed to every connected client when an admin toggles platform maintenance
 * mode, so open sessions update their banner without waiting for a reload.
 * Fresh loads read the same state from `GET /config` instead.
 */
export const WsMaintenanceSchema = z.object({
  type: z.literal('maintenance'),
  maintenance: MaintenanceStateSchema,
});

/** Server reply to a client {@link WsPingSchema} liveness probe. */
export const WsPongSchema = z.object({
  type: z.literal('pong'),
});

export const WsFlowEventSchema = z.object({
  type: z.literal('flow_event'),
  flowEventType: z.string(),
  payload: z.unknown(),
});

/**
 * Generic plugin → web real-time push. A plugin's server code broadcasts
 * one of these via `ctx.services.broadcastService.broadcastPluginEvent(...)`
 * and its web surface consumes them with `usePluginEvent(pluginName,
 * eventType, handler)` from `@reflex/ui/client/socket`. Org-scoped like the
 * other broadcasts: only clients whose active org matches receive it. The
 * `payload` shape is owned by the emitting plugin (the core stays generic —
 * it never needs to know what `eventType` means). Modeled on
 * {@link WsFlowEventSchema}; reach for this instead of adding a new core
 * message type for every plugin that needs a real-time ping.
 */
export const WsPluginEventSchema = z.object({
  type: z.literal('plugin_event'),
  pluginName: z.string(),
  eventType: z.string(),
  payload: z.unknown(),
});

/**
 * Emitted by the server whenever a saved replay's data or metadata is
 * updated (CRUD endpoint or MCP tool). The client's replay editor page
 * subscribes to this to invalidate its React Query cache so the viewer
 * re-renders after each mutation.
 */
export const WsReplayUpdatedSchema = z.object({
  type: z.literal('replay_updated'),
  replayId: z.string(),
});

export const AgentConnectivityStateSchema = z.enum(['live', 'reconnecting', 'degraded']);
export type AgentConnectivityState = z.infer<typeof AgentConnectivityStateSchema>;

export const WsAgentConnectivitySchema = z.object({
  type: z.literal('agent:connectivity'),
  agentId: z.string(),
  state: AgentConnectivityStateSchema,
  since: z.number().optional(),
  reason: z.string().optional(),
});

/**
 * Ephemeral "working" vs "idle" signal, driven by Claude turn boundaries
 * (see `AgentTurnStartedEvent`/`AgentTurnEndedEvent` on the lifecycle
 * bus). The durable agent row stays at `status: 'running'` across turns
 * so the devbox survives follow-up messages; this message is how the UI
 * distinguishes a turn in flight from an idle devbox waiting for input.
 *
 * Unlike `agent:connectivity` — which has an implicit client-side default
 * (`live`) and so only tracks deviations — the server tracks BOTH states
 * here and replays them via `agent:activity_snapshot`. `idle` is sticky
 * for the arbitrarily long stretch between turns, so a fresh connection
 * has to be told about it rather than inferring it.
 */
export const AgentActivityStateSchema = z.enum(['working', 'idle']);
export type AgentActivityState = z.infer<typeof AgentActivityStateSchema>;

export const WsAgentActivitySchema = z.object({
  type: z.literal('agent:activity'),
  agentId: z.string(),
  state: AgentActivityStateSchema,
  since: z.number().optional(),
});

/**
 * The server's complete activity table for the connection's audience,
 * pushed once per (re)connect before any incremental `agent:activity`.
 *
 * This is a REPLACE, not a merge: the client's map is module-global and
 * outlives socket drops, while the server's is in-memory and does not
 * survive a restart (and is per-replica). Replaying only the entries the
 * server happens to hold would leave an agent whose snapshot the server
 * lost pinned to the client's last-known `idle` — which
 * `unseenActivityFor` would read as finished-turn evidence for the whole
 * of a live turn. Sending the full table (including an empty one) lets
 * the client drop entries the server can no longer vouch for and fall
 * back to the same no-evidence default a fresh page load would show.
 */
export const WsAgentActivitySnapshotSchema = z.object({
  type: z.literal('agent:activity_snapshot'),
  activities: z.array(
    z.object({
      agentId: z.string(),
      state: AgentActivityStateSchema,
      since: z.number().optional(),
    }),
  ),
});

/**
 * Pushed whenever the server's queued-message list for an agent changes
 * (enqueue, edit, delete, reorder, drain). Carries the full queue so
 * clients can write straight into the React Query cache without diffing.
 */
export const WsAgentQueueUpdatedSchema = z.object({
  type: z.literal('agent:queue_updated'),
  agentId: z.string(),
  queue: z.array(QueuedMessageSchema),
});

export const WsUsageSnapshotSchema = z.object({
  type: z.literal('usage:snapshot'),
  snapshot: UsageSnapshotSchema,
});

// --- Secret-rotation progress (super-admin only stream) ---
//
// Fanned out by `WsBroadcastService` while a rotation pass is running.
// Live progress is otherwise the only way for an operator to see how a
// long-running rotation is going (the existing REST endpoint blocks
// until completion). The server publishes one `progress` event per row
// and a single `complete` event with the final `RotationResult`.
//
// Gating: super-admin only. The web client is expected to drop the
// messages on the floor when the current user isn't a super-admin
// (defence-in-depth — the server already refuses to broadcast to
// non-super sessions).

export const SecretRotationOutcomeSchema = z.enum(['rotated', 'already-current', 'failed']);
export type SecretRotationOutcome = z.infer<typeof SecretRotationOutcomeSchema>;

export const WsSecretRotationProgressSchema = z.object({
  type: z.literal('secrets:rotation:progress'),
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  name: z.string(),
  outcome: SecretRotationOutcomeSchema,
});

export const SecretRotationFailureSchema = z.object({
  name: z.string(),
  error: z.string(),
});
export type SecretRotationFailure = z.infer<typeof SecretRotationFailureSchema>;

export const SecretRotationResultSchema = z.object({
  managerId: z.string(),
  primaryKeyId: z.string(),
  rotated: z.number().int().nonnegative(),
  alreadyCurrent: z.number().int().nonnegative(),
  failed: z.array(SecretRotationFailureSchema),
  startedAt: z.number(),
  finishedAt: z.number(),
});
export type SecretRotationResult = z.infer<typeof SecretRotationResultSchema>;

export const WsSecretRotationCompleteSchema = z.object({
  type: z.literal('secrets:rotation:complete'),
  result: SecretRotationResultSchema,
});

// --- Per-org plugin installation state ---
//
// Emitted by `PluginInstallationService` after every successful state-machine
// transition (`install`, `uninstall`, `bootstrapForOrganization`,
// `resetStuckRow`). The client uses these to invalidate React Query caches
// and surface live status updates without polling.
//
// One event is emitted per affected row. When the transition is part of a
// cascade (e.g. uninstalling A also uninstalls dependents B and C), every
// emitted event carries the full `cascade: string[]` so the client knows
// which related rows to invalidate alongside the named one.
//
// Authorization: routed through the existing org-membership filter
// (`meta.organizationId === orgId || meta.isSuperAdmin`). NO per-socket
// `plugins:read` filter — the leak surface (status enum + plugin name +
// optional error string) is acceptable for v1; users without the slug
// simply can't fetch the data the events would invalidate.

export const OrgPluginInstallationStatusSchema = z.enum([
  'installing',
  'installed',
  'uninstalling',
  'uninstalled',
]);
export type OrgPluginInstallationStatus = z.infer<typeof OrgPluginInstallationStatusSchema>;

export const WsOrgPluginInstallationChangedSchema = z.object({
  type: z.literal('org:plugin_installation_changed'),
  organizationId: z.string(),
  pluginName: z.string(),
  status: OrgPluginInstallationStatusSchema,
  lastError: z.string().nullable(),
  /**
   * Plugin names touched as part of a coordinated cascade (uninstall) —
   * empty for direct single-row transitions. Lets the client invalidate
   * one query per affected row instead of subscribing to N separate
   * events.
   */
  cascade: z.array(z.string()).default([]),
});

// Per-org plugin settings writes (`PUT /organizations/:id/plugins/:name/
// settings` or `ctx.services.pluginOrgSettings.set`). The payload
// intentionally carries no values — clients holding `plugins:read`
// re-fetch through the settings endpoint, mirroring how installation
// events only carry the status enum. Same org-membership socket filter
// as `org:plugin_installation_changed`.
export const WsOrgPluginSettingsChangedSchema = z.object({
  type: z.literal('org:plugin_settings_changed'),
  organizationId: z.string(),
  pluginName: z.string(),
});

// --- Seeds import job progress (super-admin only stream) ---
//
// `POST /seeds/import` returns immediately with a `jobId`; the
// `SeedsService` runs the actual import in the background and fans out
// per-kind / per-item progress through `WsBroadcastService`. The web
// wizard subscribes via `useSeedsImportProgress(jobId)` and accumulates
// events into the live progress UI.
//
// Gating: super-admin only (same model as secret-rotation). Seeds is a
// global admin-only surface today; revisit if/when it becomes
// org-scoped.

export const SeedsImportActionSchema = z.enum([
  'created',
  'updated',
  'resolved',
  'skipped',
  'failed',
]);
export type SeedsImportAction = z.infer<typeof SeedsImportActionSchema>;

/**
 * Per-item result row carried by both `seeds:import:progress` (live) and
 * the final `seeds:import:complete` response. Kept structurally
 * identical to `SeedImportItemResult` in `@reflex/plugin-seeds`
 * so the plugin can reuse this schema for its REST/shared types.
 */
export const SeedsImportItemResultSchema = z.object({
  name: z.string(),
  action: SeedsImportActionSchema,
  newId: z.string().optional(),
  warning: z.string().optional(),
  error: z.string().optional(),
});
export type SeedsImportItemResult = z.infer<typeof SeedsImportItemResultSchema>;

export const SeedsImportResponseSchema = z.object({
  results: z.record(z.string(), z.array(SeedsImportItemResultSchema)),
  missingSecrets: z.array(z.string()),
});
export type SeedsImportResponse = z.infer<typeof SeedsImportResponseSchema>;

export const WsSeedsImportKindStartedSchema = z.object({
  type: z.literal('seeds:import:kind_started'),
  jobId: z.string(),
  kind: z.string(),
  total: z.number().int().nonnegative(),
});

export const WsSeedsImportProgressSchema = z.object({
  type: z.literal('seeds:import:progress'),
  jobId: z.string(),
  kind: z.string(),
  /** 1-based index within the current kind. */
  processed: z.number().int().nonnegative(),
  totalForKind: z.number().int().nonnegative(),
  result: SeedsImportItemResultSchema,
});

export const WsSeedsImportCompleteSchema = z.object({
  type: z.literal('seeds:import:complete'),
  jobId: z.string(),
  response: SeedsImportResponseSchema,
});

export const WsSeedsImportFailedSchema = z.object({
  type: z.literal('seeds:import:failed'),
  jobId: z.string(),
  error: z.string(),
  code: z.string().optional(),
});

/**
 * Emitted by {@link plugin-notifications} whenever a notification is
 * published. Carries only the id and audience so the client invalidates its
 * notifications cache and refetches; the full row (including the typed
 * payload) flows over REST and stays out of the WS surface.
 *
 * Audience filtering happens server-side: a row addressed to one user is
 * only sent to that user's sockets; a global broadcast (both fields null)
 * fans out to every authenticated socket; an org broadcast goes to that
 * org's sockets.
 */
export const WsNotificationCreatedSchema = z.object({
  type: z.literal('notifications:created'),
  notificationId: z.string(),
});

/**
 * Emitted when a platform admin retracts (hard-deletes) a notification.
 * Broadcast to every authenticated socket so clients remove it from their
 * cache immediately.
 */
export const WsNotificationRetractedSchema = z.object({
  type: z.literal('notifications:retracted'),
  notificationId: z.string(),
});

/**
 * Emitted to the resource owner's sockets when someone asks them to share
 * a resource (see `share-request.types.ts`). Carries only ids so the
 * client invalidates its share-request cache and refetches; the full row
 * flows over REST.
 */
export const WsShareRequestCreatedSchema = z.object({
  type: z.literal('share-request:created'),
  shareRequestId: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
});

/**
 * Emitted to the requester's sockets when their share request is approved
 * or declined. An approval means a grant now covers the resource, so the
 * client refetches the resource itself as well as the request.
 */
export const WsShareRequestResolvedSchema = z.object({
  type: z.literal('share-request:resolved'),
  shareRequestId: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  status: z.enum(['approved', 'declined']),
});

/**
 * Live sandbox metric sample fanned out by the SandboxBridgeService while
 * at least one watcher has the monitoring panel open for `agentId`. Carries
 * the thresholds the bridge enforces so the UI's recharts `ReferenceLine`
 * always matches the on-box detection.
 */
export const WsSandboxMetricsSchema = z.object({
  type: z.literal('sandbox:metrics'),
  agentId: z.string(),
  sample: SandboxMetricSampleSchema,
  thresholds: SandboxThresholdsSchema,
});

/**
 * Live `top`-style process snapshot fanned out by the SandboxBridgeService
 * while a watcher has the monitoring panel open for `agentId`. Carries a
 * curated top-N process list (latest only, not a history ring) so the process
 * viewer shows who is currently using the box's resources.
 */
export const WsSandboxProcessesSchema = z.object({
  type: z.literal('sandbox:processes'),
  agentId: z.string(),
  timestamp: z.number(),
  processes: z.array(SandboxProcessSampleSchema),
});

/**
 * Health of Reflex's control-WS connection to a box's flex-bridge:
 * - `connected`: the bridge said hello and the socket is live.
 * - `reconnecting`: the socket dropped and Reflex is re-dialing (the box may
 *   well still be up).
 * - `disconnected`: the session was torn down (box gone / no longer monitored).
 */
export const SandboxBridgeStatusSchema = z.enum(['connected', 'reconnecting', 'disconnected']);
export type SandboxBridgeStatus = z.infer<typeof SandboxBridgeStatusSchema>;

/**
 * Ephemeral on-box bridge presence + connection health for an agent. Broadcast
 * with the running flex-bridge's `version` and a `status` when it says hello,
 * updated to `reconnecting` when the control socket drops, and cleared
 * (`version: null`, `status: 'disconnected'`) when the session is torn down.
 * Replayed to new sockets the same way as `agent:connectivity`, so a page
 * refresh shows the last-known state without waiting for a reconnect. Surfaced
 * in the agent detail debug area and the sandbox monitor panel.
 */
export const WsSandboxBridgeSchema = z.object({
  type: z.literal('sandbox:bridge'),
  agentId: z.string(),
  version: z.string().nullable(),
  status: SandboxBridgeStatusSchema,
});

export const WsServerMessageSchema = z.discriminatedUnion('type', [
  WsEventMessageSchema,
  WsAgentUpdatedSchema,
  WsAgentCreatedSchema,
  WsAgentDeletedSchema,
  WsAgentDraftCreatedSchema,
  WsAgentDraftUpdatedSchema,
  WsAgentDraftDeletedSchema,
  WsAgentConnectivitySchema,
  WsAgentActivitySchema,
  WsAgentActivitySnapshotSchema,
  WsAgentQueueUpdatedSchema,
  WsSubscribedSchema,
  WsUnsubscribedSchema,
  WsErrorSchema,
  WsVersionSchema,
  WsMaintenanceSchema,
  WsPongSchema,
  WsFlowEventSchema,
  WsPluginEventSchema,
  WsReplayUpdatedSchema,
  WsUsageSnapshotSchema,
  WsSecretRotationProgressSchema,
  WsSecretRotationCompleteSchema,
  WsOrgPluginInstallationChangedSchema,
  WsOrgPluginSettingsChangedSchema,
  WsSeedsImportKindStartedSchema,
  WsSeedsImportProgressSchema,
  WsSeedsImportCompleteSchema,
  WsSeedsImportFailedSchema,
  WsNotificationCreatedSchema,
  WsNotificationRetractedSchema,
  WsShareRequestCreatedSchema,
  WsShareRequestResolvedSchema,
  WsSandboxMetricsSchema,
  WsSandboxProcessesSchema,
  WsSandboxBridgeSchema,
]);
export type WsServerMessage = z.infer<typeof WsServerMessageSchema>;
